<template>
  <div class="file-uploader-wrapper"
       :class="[{'is-uploading': isUploading},themeClass]">
    <div
      class="file-uploader"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      :class="{
        'dragging': isDragging,
        'error': error,
        'disabled': disabled,
      }"
    >
      <input
        type="file"
        ref="fileInput"
        @change="onFilesSelected"
        style="display: none;"
        :multiple="multiple"
        :disabled="disabled || isUploading"
      />
      <div class="upload-area" @click="triggerFileInput">
        <div class="loader-wrapper" v-if="isUploading" v-cloak>
          <span class="loader"></span>
          <p>Uploading...</p>
        </div>
        <div class="placeholder" v-else>
          <svg xmlns="http://www.w3.org/2000/svg"
               width="19"
               height="19"
               viewBox="0 0 19 19"
               fill="none"
          >
            <path d="M4.84057 5.60572C4.69611 5.46107 4.61496 5.265 4.61496 5.06058C4.61496
            4.85615 4.69611 4.66008 4.84057 4.51543L8.95486 0.401166C9.0262 0.331439 9.10991
            0.275628 9.20171 0.236595C9.29588 0.195834 9.39739 0.174805 9.5 0.174805C9.60261
            0.174805 9.70412 0.195834 9.79829 0.236595C9.89009 0.275628 9.9738 0.331439 10.0451
            0.401166L14.1594 4.51543C14.3039 4.66008 14.385 4.85615 14.385 5.06058C14.385
            5.265 14.3039 5.46107 14.1594 5.60572C14.0148 5.75018 13.8187 5.83132 13.6143
            5.83132C13.4099 5.83132 13.2138 5.75018 13.0691 5.60572L10.2714
            2.80801V13.2891C10.2714 13.4937 10.1902 13.6899 10.0455 13.8346C9.90081 13.9793
            9.7046 14.0605 9.5 14.0605C9.29541 14.0605 9.09919 13.9793 8.95452 13.8346C8.80985
            13.6899 8.72857 13.4937 8.72857 13.2891V2.80801L5.93086 5.60572C5.78621 5.75018 5.59014
            5.83132 5.38571 5.83132C5.18129 5.83132 4.98521 5.75018 4.84057 5.60572ZM17.7286
            12.5177C17.5248 12.5204 17.3301 12.6025 17.186 12.7466C17.0419 12.8907 16.9598
            13.0853 16.9571 13.2891V16.3748C16.9571 16.443 16.9301 16.5084 16.8818 16.5566C16.8336
            16.6049 16.7682 16.632 16.7 16.632H2.3C2.2318 16.632 2.1664 16.6049 2.11817
            16.5566C2.06995 16.5084 2.04286 16.443 2.04286 16.3748V13.2891C2.04286 13.0845
            1.96158 12.8883 1.81691 12.7436C1.67224 12.599 1.47602 12.5177 1.27143 12.5177C1.06683
            12.5177 0.870617 12.599 0.725946 12.7436C0.581275 12.8883 0.5 13.0845 0.5
            13.2891V16.3748C0.5027 16.8514 0.693209 17.3076 1.03019 17.6446C1.36717 17.9816
            1.82344 18.1721 2.3 18.1748H16.7C17.1766 18.1721 17.6328 17.9816 17.9698
            17.6446C18.3068 17.3076 18.4973 16.8514 18.5 16.3748V13.2891C18.4973 13.0853
            18.4152 12.8907 18.2711 12.7466C18.127 12.6025 17.9323 12.5204 17.7286 12.5177Z"
                  :fill="iconColorOnTheme"
            />
          </svg>
          <p>Select file or drag it here</p>
        </div>
      </div>
    </div>
    <div>
      <ul v-if="instantUpload">
        <li v-for="(filename) in uploadedFilenames" :key="filename">{{ filename }}</li>
      </ul>
      <ul v-else>
        <li v-for="(file) in files" :key="file.name">
          <span>{{ file.name }}</span>
          <button @click="removeFile(file.name)" class="remove-btn">&times;</button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'NwxFileUploader',
    model: {
      prop: 'selectedFiles',
      event: 'fileUploader:changed',
    },
    props: {
      theme: {
        type: String,
        default: 'classic',
        validator: (themeVersion) => (['classic', 'pp-modern'].includes(themeVersion)),
      },
      uploadedFilenames: {
        type: Array,
        default: () => [],
      },
      multiple: {
        type: Boolean,
        default: false,
      },
      error: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      isUploading: {
        type: Boolean,
        default: false,
      },
      instantUpload: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        files: [],
        isDragging: false,
      };
    },
    watch: {
      error(isError) {
        if (isError && this.instantUpload) {
          this.files = [];

          this.updateState();
        }
      },
    },
    computed: {
      iconColorOnTheme() {
        let iconColor = '';
        switch (this.safeTheme) {
        case 'classic':
          iconColor = '#0068DA'; // @mainBlue
          break;
        case 'pp-modern':
          iconColor = '#5851DB'; // @vigilantBlue
          break;
        default:
        }
        return iconColor;
      },
      safeTheme() { // guarantee a safe value
        const allowed = ['classic', 'pp-modern'];
        return allowed.includes(this.theme) ? this.theme : 'classic';
      },
      themeClass() {
        return `theme--${this.safeTheme}`;
      },
      filenames() {
        return this.files.map((file) => file.name);
      },
    },
    methods: {
      triggerFileInput() {
        this.$refs.fileInput.click();
      },
      onFilesSelected(e) {
        const selectedFiles = Array.from(e.target.files);
        this.handleFiles(selectedFiles);
      },
      onDragOver() {
        this.isDragging = true;
      },
      onDragLeave() {
        this.isDragging = false;
      },
      onDrop(e) {
        if (!this.isUploading || !this.disabled) {
          const droppedFiles = this.multiple
            ? Array.from(e.dataTransfer.files)
            : [e.dataTransfer.files[0]];

          this.handleFiles(droppedFiles);
          this.isDragging = false;
        }
      },
      removeFile(filename) {
        this.files = this.files.filter((file) => file.name !== filename);

        this.updateState();
      },
      handleFiles(newFiles) {
        const filteredDuplicateNames = newFiles.filter(
          (file) => !this.filenames.includes(file.name),
        );

        this.files = this.files.concat(filteredDuplicateNames);

        this.updateState();
      },
      updateState() {
        this.$emit('fileUploader:changed', this.files);
        this.$emit('change');
      },
    },
  };
</script>

<style lang="less" scoped>
@import '../assets/variables';

// classic theme
.file-uploader-wrapper {
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin: 0;
    font-family: 'Open Sans', sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 175%;
  }

  .remove-btn {
    width: auto;
    padding: 0 5px;
    background: none;
    color: @mainRed;
    margin-left: 3px;
    border: none;
    font-size: 18px;
  }

  &.is-uploading {
    .loader-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      .loader {
        width: 19px;
        height: 19px;
        border: 3px solid #0068DA;
        border-left-color: transparent;
        border-radius: 50%;
      }

      .loader {
        border: 3px solid #0068DA;
        border-left-color: transparent;
        width: 19px;
        height: 19px;
      }

      .loader {
        border: 3px solid #0068DA;
        border-left-color: transparent;
        width: 19px;
        height: 19px;
        animation: spin 1s linear infinite;
      }
    }
  }
}

.file-uploader {
  border-radius: 5px;
  border: 1px solid #C9CCCF;
  background: #ffffff;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.3s ease;

  &.error {
    border-color: @mainRed;
  }

  &:hover {
    border-color: @labelGray;
  }

  &:focus {
    border-color: @mainBlue;
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;

    .upload-area {
      p {
        color: @labelGray;
      }

      svg path {
        fill: @labelGray;
      }
    }
  }

  &.dragging {
    border-color: #0068DA;
  }

  .upload-area {
    height: 128px;
    padding: 40px;

    p {
      margin: 0;
      color: #0068DA;
      text-align: center;
      font-family: 'Open Sans', sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 175%;
    }
  }
}

// pp-modern theme
.file-uploader-wrapper.theme--pp-modern {
  .file-uploader{
    border-radius: 0;
    background: @accessWhite;
    .upload-area p{
      color: @vigilantBlue;
    }
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
